<template>
  <section class="job-offers-section">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <div class="section-header">
            <h2 class="section-title">DERNIERES OFFRES</h2>
            <div class="title-underline"></div>
            <p v-if="showingPastOffers" class="past-offers-notice">
              Aucune offre active pour le moment. Voici nos dernieres missions realisees.
            </p>
          </div>

          <div class="job-offers-container">
            <div class="navigation-arrows">
              <button class="nav-arrow prev" @click="previousSlide" :disabled="currentIndex === 0">
                <i class="fas fa-chevron-left"></i>
              </button>
              <button class="nav-arrow next" @click="nextSlide" :disabled="currentIndex >= maxIndex">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>

            <div class="job-offers-slider" ref="slider">
              <div class="job-offers-track" :style="{ transform: `translateX(-${currentIndex * slideWidth}px)` }">
                <div
                  v-for="offer in jobOffers"
                  :key="offer.id"
                  class="job-offer-card"
                  :class="{ 'past-offer': isExpired(offer.deadline) || !offer.is_active }"
                >
                  <!-- Image banner -->
                  <div class="card-image" @click="viewDetails(offer.id)">
                    <img :src="recruteImg" alt="PREFERE PLUS recrute" />
                    <span v-if="isExpired(offer.deadline) || !offer.is_active" class="badge-past">Mission cloturee</span>
                    <span v-else class="badge-active">En cours</span>
                  </div>

                  <!-- Card content -->
                  <div class="card-content">
                    <h3 class="job-title" @click="viewDetails(offer.id)">{{ offer.title }}</h3>

                    <div class="offer-details">
                      <div class="detail-item">
                        <i class="fas fa-calendar-alt"></i>
                        <span>{{ formatDate(offer.deadline) }}</span>
                      </div>
                      <div v-if="!isExpired(offer.deadline) && offer.is_active" class="detail-item highlight">
                        <i class="fas fa-clock"></i>
                        <span>{{ getRemainingDays(offer.deadline) }} jour(s) restant(s)</span>
                      </div>
                    </div>

                    <div class="card-actions">
                      <div class="action-buttons">
                        <button class="btn btn-details" @click="viewDetails(offer.id)">
                          <i class="fas fa-eye me-1"></i> Details
                        </button>
                        <button v-if="!isExpired(offer.deadline) && offer.is_active" class="btn btn-apply" @click="applyNow(offer.id)">
                          <i class="fas fa-paper-plane me-1"></i> Postuler
                        </button>
                        <button
                          v-if="offer.pdf_file"
                          class="btn btn-pdf"
                          @click="downloadPdf(offer)"
                          :disabled="downloadingPdf === offer.id"
                        >
                          <i class="fas fa-file-pdf"></i>
                        </button>
                      </div>

                      <div class="share-buttons">
                        <button class="share-btn whatsapp" @click="shareOnWhatsApp(offer)" title="WhatsApp">
                          <i class="fab fa-whatsapp"></i>
                        </button>
                        <button class="share-btn facebook" @click="shareOnFacebook(offer)" title="Facebook">
                          <i class="fab fa-facebook-f"></i>
                        </button>
                        <button class="share-btn linkedin" @click="shareOnLinkedIn(offer)" title="LinkedIn">
                          <i class="fab fa-linkedin-in"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="text-center mt-4">
            <router-link to="/job-offers" class="btn-see-all">
              Voir toutes les offres <i class="fas fa-arrow-right ms-2"></i>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import recruteImg from '../assets/img/on-recrute.png'

export default {
  name: 'JobOffersSection',

  data() {
    return {
      jobOffers: [],
      loading: true,
      showingPastOffers: false,
      currentIndex: 0,
      slideWidth: 0,
      downloadingPdf: null,
      maxIndex: 0,
      recruteImg
    }
  },

  async mounted() {
    this.loading = true
    setTimeout(async () => {
      await this.loadJobOffers()
      this.calculateSlideWidth()
      window.addEventListener('resize', this.calculateSlideWidth)
    }, 100)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.calculateSlideWidth)
  },

  methods: {
    async loadJobOffers() {
      try {
        const cachedOffers = localStorage.getItem('jobOffersSectionCacheV2')
        const cacheTimestamp = localStorage.getItem('jobOffersSectionCacheV2Timestamp')
        const now = Date.now()

        let allOffers = []

        if (cachedOffers && cacheTimestamp && (now - parseInt(cacheTimestamp)) < 300000) {
          allOffers = JSON.parse(cachedOffers)
        } else {
          const response = await this.$http.get('/jobs')
          allOffers = response.data.job_offers.data
          localStorage.setItem('jobOffersSectionCacheV2', JSON.stringify(allOffers))
          localStorage.setItem('jobOffersSectionCacheV2Timestamp', now.toString())
        }

        const activeOffers = allOffers.filter(offer =>
          offer.is_active && !this.isExpired(offer.deadline)
        )

        if (activeOffers.length > 0) {
          this.jobOffers = activeOffers
          this.showingPastOffers = false
        } else {
          const pastOffers = allOffers
            .filter(offer => !offer.is_active || this.isExpired(offer.deadline))
            .slice(0, 6)
          this.jobOffers = pastOffers
          this.showingPastOffers = pastOffers.length > 0
        }

        this.maxIndex = Math.max(0, this.jobOffers.length - this.getVisibleSlides())
      } catch (error) {
        console.error('Erreur lors du chargement des offres:', error)
        if (error.response?.status === 401) {
          this.$router.push('/login')
        }
      } finally {
        this.loading = false
      }
    },

    calculateSlideWidth() {
      const container = this.$refs.slider
      if (container) {
        const containerWidth = container.offsetWidth
        const visibleSlides = this.getVisibleSlides()
        this.slideWidth = containerWidth / visibleSlides
      }
    },

    getVisibleSlides() {
      if (window.innerWidth >= 1200) return 3
      if (window.innerWidth >= 768) return 2
      return 1
    },

    previousSlide() {
      if (this.currentIndex > 0) {
        this.currentIndex--
      }
    },

    nextSlide() {
      if (this.currentIndex < this.maxIndex) {
        this.currentIndex++
      }
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getRemainingDays(date) {
      const deadline = new Date(date)
      const today = new Date()
      const diffTime = deadline - today
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return Math.max(0, diffDays)
    },

    isExpired(date) {
      return new Date(date) < new Date()
    },

    viewDetails(offerId) {
      this.$router.push(`/job-offers/${offerId}`)
    },

    applyNow(offerId) {
      if (!localStorage.getItem('token')) {
        this.$router.push('/login')
        return
      }
      this.$router.push(`/jobs/${offerId}/apply`)
    },

    async downloadPdf(offer) {
      if (!offer.pdf_file) return

      this.downloadingPdf = offer.id
      try {
        const response = await this.$http.get(`/storage/${offer.pdf_file}`, {
          responseType: 'blob'
        })

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', offer.pdf_file.split('/').pop())
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Erreur lors du telechargement:', error)
        alert('Erreur lors du telechargement du PDF')
      } finally {
        this.downloadingPdf = null
      }
    },

    shareOnWhatsApp(offer) {
      const text = `Offre d'emploi: ${offer.title} - PREFERE PLUS`
      const url = `${window.location.origin}/job-offers/${offer.id}`
      window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`)
    },

    shareOnFacebook(offer) {
      const url = `${window.location.origin}/job-offers/${offer.id}`
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)
    },

    shareOnLinkedIn(offer) {
      const url = `${window.location.origin}/job-offers/${offer.id}`
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`)
    }
  }
}
</script>

<style scoped>
.job-offers-section {
  padding: 60px 0;
  background-color: #f8f9fa;
}

.section-header {
  text-align: center;
  margin-bottom: 40px;
}

.section-title {
  color: #1d9ed9;
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 10px;
  font-family: 'Poppins', sans-serif;
}

.title-underline {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #29b3cd, #e95d16);
  margin: 0 auto;
  border-radius: 2px;
}

.past-offers-notice {
  color: #6c757d;
  font-size: 0.95rem;
  margin-top: 12px;
  font-style: italic;
}

.job-offers-container {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  overflow: hidden;
}

.navigation-arrows {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  z-index: 10;
  pointer-events: none;
}

.nav-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 2px 10px rgba(0,0,0,0.15);
  cursor: pointer;
  pointer-events: auto;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-arrow:hover {
  background-color: #1d9ed9;
  color: white;
}

.nav-arrow:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.nav-arrow.prev {
  left: 5px;
}

.nav-arrow.next {
  right: 5px;
  background-color: #e95d16;
  color: white;
}

.nav-arrow.next:hover {
  background-color: #d04a0a;
}

.job-offers-slider {
  overflow: hidden;
  position: relative;
}

.job-offers-track {
  display: flex;
  transition: transform 0.5s ease;
}

/* === Card Design === */
.job-offer-card {
  flex: 0 0 calc(33.333% - 20px);
  margin: 0 10px;
  background: white;
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #eef2f6;
}

.job-offer-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 35px rgba(0,0,0,0.14);
}

.past-offer {
  opacity: 0.88;
}

.past-offer .card-image img {
  filter: grayscale(30%);
}

/* Image banner */
.card-image {
  position: relative;
  cursor: pointer;
  overflow: hidden;
}

.card-image img {
  width: 100%;
  height: 180px;
  object-fit: cover;
  display: block;
  transition: transform 0.4s ease;
}

.job-offer-card:hover .card-image img {
  transform: scale(1.04);
}

.badge-past,
.badge-active {
  position: absolute;
  top: 12px;
  left: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 20px;
  letter-spacing: 0.5px;
}

.badge-past {
  background-color: rgba(108, 117, 125, 0.9);
  color: #fff;
}

.badge-active {
  background-color: rgba(40, 167, 69, 0.9);
  color: #fff;
}

/* Card content */
.card-content {
  padding: 18px 20px 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.job-title {
  color: #2c3e50;
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0 0 12px;
  line-height: 1.35;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition: color 0.2s;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.job-title:hover {
  color: #1d9ed9;
}

.offer-details {
  margin-bottom: 14px;
}

.detail-item {
  display: flex;
  align-items: center;
  font-size: 0.82rem;
  color: #6c757d;
  margin-bottom: 6px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-item i {
  margin-right: 8px;
  color: #29b3cd;
  width: 14px;
  font-size: 0.8rem;
}

.detail-item.highlight {
  color: #e95d16;
  font-weight: 600;
}

.detail-item.highlight i {
  color: #e95d16;
}

.card-actions {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.action-buttons {
  display: flex;
  gap: 6px;
}

.btn {
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Poppins', sans-serif;
}

.btn-details {
  background-color: #29b3cd;
  color: white;
}

.btn-details:hover {
  background-color: #1d9ed9;
}

.btn-apply {
  background-color: #e95d16;
  color: white;
}

.btn-apply:hover {
  background-color: #d04a0a;
}

.btn-pdf {
  background-color: #f8f9fa;
  color: #dc3545;
  border: 1px solid #dee2e6;
  padding: 7px 10px;
}

.btn-pdf:hover {
  background-color: #dc3545;
  color: white;
  border-color: #dc3545;
}

.share-buttons {
  display: flex;
  gap: 5px;
}

.share-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.75rem;
}

.share-btn.whatsapp {
  background-color: #25d366;
  color: white;
}

.share-btn.facebook {
  background-color: #1877f2;
  color: white;
}

.share-btn.linkedin {
  background-color: #0077b5;
  color: white;
}

.share-btn:hover {
  transform: scale(1.15);
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

/* See all link */
.btn-see-all {
  display: inline-flex;
  align-items: center;
  color: #29b3cd;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 0.3s;
  font-family: 'Poppins', sans-serif;
}

.btn-see-all:hover {
  color: #e95d16;
  gap: 4px;
}

/* Responsive */
@media (max-width: 1199px) {
  .job-offer-card {
    flex: 0 0 calc(50% - 20px);
  }
}

@media (max-width: 767px) {
  .job-offer-card {
    flex: 0 0 calc(100% - 20px);
  }

  .section-title {
    font-size: 1.8rem;
  }
}

@media (max-width: 575px) {
  .section-title {
    font-size: 1.4rem;
  }

  .job-offers-section {
    padding: 40px 0;
  }

  .card-image img {
    height: 150px;
  }

  .card-content {
    padding: 14px 16px 16px;
  }

  .card-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .action-buttons {
    justify-content: center;
  }

  .share-buttons {
    justify-content: center;
  }
}
</style>
